import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getBranches, filterBillsList, formatDateForApi } from '../../utilities/api';

// Async thunk to fetch the branch list. Uses the same endpoint as the Sales,
// Item and Category reports so every report filter offers the same branches.
export const fetchFilterOptions = createAsyncThunk(
    'billReports/fetchFilterOptions',
    async ({ clientId }, { rejectWithValue }) => {
        try {
            const response = await getBranches(clientId);
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch branches');
        }
    }
);

// Async thunk to fetch bills data
export const fetchBillsData = createAsyncThunk(
    'billReports/fetchBillsData',
    async ({ fromDate, toDate, branchId, status }, { rejectWithValue }) => {
        try {
            // No branch chosen means "every branch of this client", which the API
            // expresses as branch_id "All" scoped by client_id.
            const resolvedBranchId = branchId || 'All';
            const payload = {
                from_date: fromDate ? formatDateForApi(fromDate) : '',
                to_date: toDate ? formatDateForApi(toDate) : '',
                branch_id: resolvedBranchId,
                status: status || ''
            };
            const clientId = localStorage.getItem('clientid');
            if (clientId) payload.client_id = clientId;
            return await filterBillsList(payload);
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch bills data');
        }
    }
);

const initialState = {
    // Filter options
    branches: [],

    // Selected filters
    selectedBranch: null,
    selectedStatus: null,
    fromDate: null,
    toDate: null,
    
    // Bill data
    billData: [],

    // Loading states
    loading: false,
    filterOptionsLoading: false,
    
    // Error states
    error: null,
    filterOptionsError: null
};

const billReportsSlice = createSlice({
    name: 'billReports',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },
        setSelectedStatus: (state, action) => {
            state.selectedStatus = action.payload;
        },
        setFilters: (state, action) => {
            const { fromDate, toDate, selectedBranch, selectedStatus } = action.payload;
            if (fromDate !== undefined) state.fromDate = fromDate;
            if (toDate !== undefined) state.toDate = toDate;
            if (selectedBranch !== undefined) state.selectedBranch = selectedBranch;
            if (selectedStatus !== undefined) state.selectedStatus = selectedStatus;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.selectedStatus = null;
            state.billData = [];
            state.error = null;
        },
        clearBillData: (state) => {
            state.billData = [];
        },
        clearError: (state) => {
            state.error = null;
            state.filterOptionsError = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch filter options
        builder
            .addCase(fetchFilterOptions.pending, (state) => {
                state.filterOptionsLoading = true;
                state.filterOptionsError = null;
            })
            .addCase(fetchFilterOptions.fulfilled, (state, action) => {
                state.filterOptionsLoading = false;
                const rawBranches = Array.isArray(action.payload) ? action.payload : [];
                const hasAll = rawBranches.some((b) => String(b?.branch_id).toLowerCase() === 'all');
                state.branches = hasAll
                    ? rawBranches
                    : [{ branch_id: 'All', branch_name: 'All Branches' }, ...rawBranches];
            })
            .addCase(fetchFilterOptions.rejected, (state, action) => {
                state.filterOptionsLoading = false;
                state.filterOptionsError = action.payload;
                // Keep "All Branches" usable so the report still covers every branch
                state.branches = [{ branch_id: 'All', branch_name: 'All Branches' }];
            });

        // Fetch bills data
        builder
            .addCase(fetchBillsData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.billData = [];
            })
            .addCase(fetchBillsData.fulfilled, (state, action) => {
                state.loading = false;
                state.billData = action.payload;
            })
            .addCase(fetchBillsData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.billData = [];
            });
    }
});

// Export actions
export const {
    setFromDate,
    setToDate,
    setSelectedBranch,
    setSelectedStatus,
    setFilters,
    resetFilters,
    clearBillData,
    clearError
} = billReportsSlice.actions;

// Selectors
export const selectBillReports = (state) => state.billReports;
export const selectBillData = (state) => state.billReports.billData;
export const selectLoading = (state) => state.billReports.loading;
export const selectBranches = (state) => state.billReports.branches;
export const selectFilterOptionsLoading = (state) => state.billReports.filterOptionsLoading;
export const selectFilterOptionsError = (state) => state.billReports.filterOptionsError;
export const selectSelectedBranch = (state) => state.billReports.selectedBranch;
export const selectSelectedStatus = (state) => state.billReports.selectedStatus;
export const selectFromDate = (state) => state.billReports.fromDate;
export const selectToDate = (state) => state.billReports.toDate;
export const selectError = (state) => state.billReports.error;

export default billReportsSlice.reducer;

